import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const LIST_SEARCH_COMMIT_MS = 450;

export const LIST_SEARCH_MIN_CHARS = 3;

export type CommitListSearchOptions = {
  minChars?: number;
  resetPage?: boolean;
};

export default function useSearchParamsOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [internalSearchParams, setInternalSearchParams] = useState(searchParams);

  const replace = useCallback(
    (field: string, value: string) => {
      setInternalSearchParams((oldSearchParams) => {
        const newSearchParams = new URLSearchParams(oldSearchParams);
        newSearchParams.delete(field);
        newSearchParams.append(field, value);
        return newSearchParams;
      });
    },
    [setInternalSearchParams]
  );

  const deleteFieldInArrayUsingPrefix = useCallback(
    (field: string, prefix: string) => {
      setInternalSearchParams((oldSearchParams) => {
        const newSearchParams = new URLSearchParams(oldSearchParams);
        const fieldArrayValues = newSearchParams.getAll(field);
        newSearchParams.delete(field);
        fieldArrayValues
          .filter((v) => !v.includes(prefix))
          .forEach((v) => {
            newSearchParams.append(field, v);
          });
        return newSearchParams;
      });
    },
    [setInternalSearchParams]
  );

  const replaceInArrayField = useCallback(
    (field: string, oldValue: string, newValue: string) => {
      setInternalSearchParams((oldSearchParams) => {
        const newSearchParams = new URLSearchParams(oldSearchParams);
        const fieldArrayValues = newSearchParams.getAll(field);
        newSearchParams.delete(field);
        fieldArrayValues
          .filter((v) => v !== oldValue)
          .forEach((v) => {
            newSearchParams.append(field, v);
          });
        newSearchParams.append(field, newValue);
        return newSearchParams;
      });
    },
    [setInternalSearchParams]
  );

  const replaceInArrayFieldUsingPrefix = useCallback(
    (field: string, oldValuePrefix: string, newValue: string) => {
      setInternalSearchParams((oldSearchParams) => {
        const newSearchParams = new URLSearchParams(oldSearchParams);
        const fieldArrayValues = newSearchParams.getAll(field);
        if (fieldArrayValues.find((v) => v.includes(oldValuePrefix)) === newValue) return newSearchParams;
        newSearchParams.delete(field);
        fieldArrayValues
          .filter((v) => !v.includes(oldValuePrefix))
          .forEach((v) => {
            newSearchParams.append(field, v);
          });
        newSearchParams.append(field, newValue);
        return newSearchParams;
      });
    },
    [setInternalSearchParams]
  );

  useEffect(() => {
    setSearchParams(internalSearchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalSearchParams]);

  const commitListSearchSync = useCallback((raw: string, opts?: CommitListSearchOptions) => {
    const minChars = opts?.minChars ?? LIST_SEARCH_MIN_CHARS;
    const resetPage = opts?.resetPage ?? true;
    setInternalSearchParams((oldSearchParams) => {
      const next = new URLSearchParams(oldSearchParams);
      const trimmed = raw.trim();
      next.delete("search");
      if (trimmed.length >= minChars) {
        next.append("search", trimmed);
      }
      if (resetPage) {
        next.delete("page[number]");
        next.append("page[number]", "1");
      }
      return next;
    });
  }, []);

  const commitListSearch = useMemo(
    () =>
      debounce((raw: string, opts?: CommitListSearchOptions) => {
        commitListSearchSync(raw, opts);
      }, LIST_SEARCH_COMMIT_MS),
    [commitListSearchSync]
  );

  useEffect(
    () => () => {
      commitListSearch.cancel();
    },
    [commitListSearch]
  );

  const cancelCommitListSearch = useCallback(() => {
    commitListSearch.cancel();
  }, [commitListSearch]);

  return {
    replace,
    deleteFieldInArrayUsingPrefix,
    replaceInArrayField,
    replaceInArrayFieldUsingPrefix,
    searchParams,
    commitListSearch,
    commitListSearchSync,
    cancelCommitListSearch,
  };
}
